import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { getDistributionZones } from "graphQL/query/distributionZones";
// eslint-disable-next-line import/no-unresolved -- JS query module resolved at build time
import { getCampaignCost } from "graphQL/query/campaign";
import { getSelfServeCampaignData } from "graphQL/query/selfServeCampaignData";
import { updateSelfServeCampaignData } from "graphQL/mutations/selfServeCampaignData";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
// eslint-disable-next-line import/no-unresolved -- JS module resolved at build time
import { registerTargetingReload } from "copilot/agentEvents";
import { useClientConfig } from "campaign-builder/context/ClientConfigContext";
import { MAP_TYPES } from "pages/constants";
import { getTargetingCities, TargetingCity } from "./cities";
import { useSaveStatus, trackSave } from "./saveStatus";
import {
  packSchedule,
  unpackSchedule,
  countPairs,
  scheduleHasOutdatedDate,
  type AreaScheduleEntry,
} from "campaign-builder/state/h2hSchedule";
import { getEarliestLaunchDate } from "campaign-builder/state/launchDate";
import { useMinLeadTimeBusinessDays } from "campaign-builder/hooks/useMinLeadTimeBusinessDays";
import {
  resolveCityLimits,
  resolveMaximum,
  getMinLimitMessage,
  getMaxLimitMessage,
  countForGate,
  countDistributors,
  maxWeeklyDistributors,
  type LimitMessage,
} from "campaign-builder/state/targetingLimits";
import type { ServerDetailedCost } from "campaign-builder/channels/flyering/order-details/cost/costPreview";
import { getMinimumCampaignValue, isBelowMinimumCampaignValue } from "utils/minimumCampaignValue";
import { useTargetingGateReporter, type TargetingGate } from "./targetingGate";
import { useTargetingFocus } from "./targetingFocus";

// A distribution area recommended for the city. H2H (pins) and D2D (polygons)
// share this shape; the channel-specific map content that consumes it lands in
// F4/F5.
export interface RecommendedZone {
  id: string;
  value: string;
  type: string;
  areaType: string;
  radius: number | null;
  center: { lat: number; lng: number } | null;
  paths: unknown;
}

// An area the user has selected. H2H carries the pin coordinates; D2D carries
// the polygon paths (and the polygon centre in lat/lng) so the map can re-mark
// the selection on rehydrate and recentre on focus. units is auto-derived from
// the area's coverage (D2D mission-boundary delivery count) — never user-entered.
export interface TargetingArea {
  id: string;
  value: string;
  units?: number;
  lat?: number;
  lng?: number;
  type?: string;
  /** D2D polygon rings ([{lat,lng}…]); absent for H2H pins. */
  paths?: unknown;
  /**
   * H2H per-area distribution scheduling (F6): one entry per selected date, each
   * with its own set of time slots. Flattened to the address `startDate`/`time`
   * `#`-strings on save and regrouped on load (see state/h2hSchedule). Absent for D2D.
   */
  schedule?: AreaScheduleEntry[];
  /**
   * H2H custom point created from the map search ("create new ones"): a pin at a
   * searched address rather than a recommended catalogue location. Persists with
   * isCustomLocationName/isFixedLocation set the legacy way so downstream treats
   * it as a self-serve location. Absent for recommended pins and D2D.
   */
  custom?: boolean;
}

type TargetingChannel = "H2H" | "D2D";

interface TargetingContextValue {
  channel: TargetingChannel;
  cities: TargetingCity[];
  citiesLoading: boolean;
  citiesError: boolean;
  selectedCity: TargetingCity | null;
  selectCity: (city: TargetingCity) => void;
  /** True while recommended areas/zones load after a city is picked (AC19). */
  isAreasLoading: boolean;
  /** Recommended areas for the selected city — consumed by the F4/F5 map. */
  recommendedZones: RecommendedZone[];
  /** Areas the user has selected. F4/F5 populate this; F3 lists + counts them. */
  selectedAreas: TargetingArea[];
  setSelectedAreas: React.Dispatch<React.SetStateAction<TargetingArea[]>>;
  /** Add a selected area and persist; no-op if already selected or no city. */
  addArea: (area: TargetingArea) => void;
  /** Remove a selected area by id and persist. */
  removeArea: (id: string) => void;
  /**
   * Replace an H2H area's distribution schedule and persist (F6). Recomputes the
   * area's distributor count (units) from the new pair total so the consolidated
   * card totals and the persisted missionCount stay in lockstep.
   */
  setAreaSchedule: (id: string, schedule: AreaScheduleEntry[]) => void;
  /**
   * Patch missing per-area units from the given id→units source and persist the
   * healed selection. Pre-redesign D2D drafts persisted no missionCount, so
   * their rehydrated areas show 0 units; the boundary layer backfills them from
   * the zone metadata (T8) once it loads, and non-US areas from findNumberHouseholds.
   * Areas with positive units, or no match in the source, are left untouched; no-op
   * when nothing heals.
   */
  backfillAreaUnits: (unitsById: Map<string, number>) => void;
  /** BAs per location (OADS) — distributors per (date,time) pair. */
  baPerLocation: number;
  /** Flyers per distributor (OADS flyer-pack) — flyers = distributors × this. */
  flyerPack: number;
  /**
   * The area just added by a user action (carries a nonce so re-adds re-fire).
   * Drives the shared "New distribution area added" toast — set only by addArea,
   * never on rehydrate or removal. The toast clears it via clearJustAdded.
   */
  justAddedArea: { id: string; nonce: number } | null;
  clearJustAdded: () => void;
  /**
   * The area card to scroll to + flash. Carries a nonce so repeating the same
   * id (e.g. clicking the same pin twice) still re-triggers the flash. The card
   * owns the flash lifetime — no clearing needed here.
   */
  highlightTarget: { id: string; nonce: number } | null;
  highlightArea: (id: string) => void;
  /** Area a list card asked the map to locate/centre on; the map clears it. */
  mapFocusAreaId: string | null;
  focusOnMap: (id: string) => void;
  clearMapFocus: () => void;
  /**
   * The area currently hovered, from either side of the map↔list pair (AC24): a
   * list card sets it on mouse-enter, a map pin/polygon sets it on mouseover.
   * Both the card (darker outline) and the map (D2D red fill) read it; null when
   * nothing is hovered. Sustained for the hover lifetime — distinct from the
   * one-shot highlightTarget/mapFocusAreaId click signals.
   */
  hoveredAreaId: string | null;
  setHoveredArea: (id: string | null) => void;
  /**
   * A point picked from the map search (carries a nonce so repeating the same
   * spot re-fires). D2D consumes it: the polygon layer finds the boundary
   * containing the point and opens its Add dialog — the search "clicks" the
   * matched area rather than only panning (AC-S3). H2H creates a custom pin
   * directly and ignores this. The consumer clears it.
   */
  mapSearchPoint: { lat: number; lng: number; nonce: number } | null;
  searchMapPoint: (point: { lat: number; lng: number }) => void;
  clearMapSearchPoint: () => void;
  /**
   * The maximum distributors/areas error (AC32), shown inline below the area
   * list. Set when a city's limit is exceeded — either an add was blocked or a
   * rehydrated draft is already over. Null when within limits.
   */
  limitError: LimitMessage | null;
}

const TargetingContext = createContext<TargetingContextValue | undefined>(undefined);

// Maps a selected area to the address shape the pathfinder endpoint accepts
// (the SelfServeCampaignData address fields). Unknown fields are rejected by
// the input type. H2H pins persist as a marker with `position`; D2D polygons
// persist as a POLYGON with `paths` + the polygon `center`. H2H per-area dates
// /times (F6) flatten into the parallel `startDate`/`time` `#`-strings.
const toAddress = (area: TargetingArea) => {
  const hasCoord = typeof area.lat === "number" && typeof area.lng === "number";
  const isPolygon = area.type === MAP_TYPES.POLYGON;
  const { startDate, time } = packSchedule(area.schedule ?? []);
  return {
    id: area.id,
    value: area.value,
    type: area.type ?? MAP_TYPES.MARKER,
    isVisible: true,
    // A searched custom point is not a fixed catalogue location; recommended
    // pins are. Polygons are never fixed locations.
    isFixedLocation: !isPolygon && !area.custom,
    isCustomLocationName: area.custom ? true : null,
    position: !isPolygon && hasCoord ? { lat: area.lat, lng: area.lng } : null,
    center: isPolygon && hasCoord ? { lat: area.lat, lng: area.lng } : null,
    paths: isPolygon && area.paths ? area.paths : [],
    radius: null,
    editMode: null,
    startDate: startDate || null,
    endDate: null,
    time: time || null,
    missionCount: typeof area.units === "number" ? area.units : null,
  };
};

// Builds the selectedCities persistence entry the pathfinder endpoint expects.
// addresses carries the selected areas (empty in F3; F4/F5 fill it). Exported so
// submission (F12) can resend the same payload alongside the campaign costs.
export const toSelectedCity = (city: TargetingCity, areas: TargetingArea[]) => ({
  id: city.id,
  isVisible: true,
  lat: city.lat,
  lng: city.lng,
  name: city.name,
  nameWithState: city.nameWithState,
  isAddressesPopupOpened: false,
  postalCode: city.postalCode,
  addresses: areas.map(toAddress),
});

// Fetches the recommended distribution zones for a city and returns the slice
// for the active channel. Network/parse failures resolve to an empty list so
// the map simply shows no recommendations rather than blocking the step.
const fetchRecommendedZones = async (cityId: number, channel: TargetingChannel): Promise<RecommendedZone[]> => {
  try {
    const request = await getDistributionZones(String(cityId));
    if (request.status !== 200) return [];
    const {
      data: { distributionZones },
    } = await request.json();
    const slice = channel === "H2H" ? distributionZones?.h2h : distributionZones?.d2d;
    return slice ?? [];
  } catch {
    return [];
  }
};

export const TargetingProvider = ({ channel, children }: { channel: TargetingChannel; children: React.ReactNode }) => {
  const { campaignId } = useCampaignData();
  const { country } = useClientConfig();
  const save = useSaveStatus();
  const queryClient = useQueryClient();

  // OADS-driven distributor/flyer factors for H2H consolidated totals (AC27, T9).
  // Same source the legacy per-(date,time) calculation reads; 0 until settings load.
  const baPerLocation = country?.countryDefaultSettings?.baPerLocation ?? country?.baPerLocation ?? 0;
  const flyerPack = country?.countryDefaultSettings?.flyerPackH2h ?? 0;

  // Saved selection lives in pathfinder, not on the campaign record, so it is
  // read through its own query and cached under this key.
  const savedQueryKey = ["targeting-saved", campaignId] as const;

  const [selectedCity, setSelectedCity] = useState<TargetingCity | null>(null);
  const [selectedAreas, setSelectedAreas] = useState<TargetingArea[]>([]);
  const [recommendedZones, setRecommendedZones] = useState<RecommendedZone[]>([]);
  const [isAreasLoading, setIsAreasLoading] = useState(false);
  const [highlightTarget, setHighlightTarget] = useState<{ id: string; nonce: number } | null>(null);
  const [mapFocusAreaId, setMapFocusAreaId] = useState<string | null>(null);
  const [hoveredAreaId, setHoveredAreaId] = useState<string | null>(null);
  const [mapSearchPoint, setMapSearchPoint] = useState<{ lat: number; lng: number; nonce: number } | null>(null);
  const [justAddedArea, setJustAddedArea] = useState<{ id: string; nonce: number } | null>(null);
  // Set when an over-the-maximum add/schedule change is blocked (AC32). Cleared
  // on the next successful mutation. Distinct from the derived over-limit state,
  // which catches a rehydrated draft that is already past the cap.
  const [blockedLimit, setBlockedLimit] = useState<LimitMessage | null>(null);

  // Per-city min/max gates (AC31/AC32). The minimum and the channel maximum are
  // server-driven per city (T7); the count compared against them is distributors
  // for H2H and areas for D2D.
  const limits = resolveCityLimits(country, selectedCity?.id ?? null);
  const maximum = resolveMaximum(channel, limits.weeklyMissionLimit);
  const gateCount = countForGate(channel, selectedAreas);
  const overMaximum =
    channel === "D2D" ? selectedAreas.length > maximum : maxWeeklyDistributors(selectedAreas, baPerLocation) > maximum;

  // Guards stale zone fetches: only the latest request's result is applied, so a
  // slow fetch for a previously-selected city can't overwrite the current city's
  // zones or prematurely clear the loading flag.
  const zoneFetchSeq = useRef(0);
  const loadRecommendedZones = useCallback(
    (cityId: number) => {
      const seq = (zoneFetchSeq.current += 1);
      setIsAreasLoading(true);
      void fetchRecommendedZones(cityId, channel).then((zones) => {
        if (seq !== zoneFetchSeq.current) return;
        setRecommendedZones(zones);
        setIsAreasLoading(false);
      });
    },
    [channel]
  );

  const {
    data: cities = [],
    isLoading: citiesLoading,
    isError: citiesError,
  } = useQuery<TargetingCity[]>({
    queryKey: ["targeting-cities", channel, country?.id],
    queryFn: () => getTargetingCities(channel, country!),
    enabled: !!country?.id,
  });

  // The campaign's previously saved city + areas, used to rehydrate on entry.
  // The provider lives above the shell (PAD-31560), so it mounts once and never
  // remounts on step switches — a single fetch on entry is enough. Mutations keep
  // this cache in sync via persistAreas' setQueryData (the saved selection is the
  // source of truth), so no refetch is needed to stay fresh.
  const { data: savedData, isFetched: savedFetched } = useQuery({
    queryKey: savedQueryKey,
    queryFn: () => getSelfServeCampaignData(campaignId!),
    enabled: !!campaignId,
  });

  // Server-authoritative cost for the current selection (F15). Shares the query
  // key + fn with the order drawer's useChannelCostPreview, so react-query serves
  // one fetch to both. The subtotal feeds the Summary figure and the below-market
  // -minimum check (AC33). Invalidated after each selection persists (see
  // persistAreas / selectCity) so the figure tracks the selection in near-real time.
  // undefined until a city is picked (query disabled) — keeps the key
  // element-identical to useChannelCostPreview's, with no cityId=0 slot.
  const costCityId = selectedCity?.id;
  const { data: detailedCost = null } = useQuery<ServerDetailedCost | null>({
    queryKey: ["channelCampaignCost", campaignId, costCityId],
    // `enabled` guarantees campaignId + city exist by the time this runs.
    queryFn: () => getCampaignCost({ id: campaignId!, cityId: costCityId! }),
    // Only once a real city is in scope — there is no cost (and no meaningful
    // Summary figure) before a city is picked, and cityId=0 is an invalid sentinel.
    enabled: !!campaignId && !!selectedCity,
    keepPreviousData: true,
  });

  // Rehydrate the selection once, after both the city list and the saved data
  // are available, so the dropdown shows the saved city and the areas list
  // restores. Re-fetches recommendations for the saved city.
  // `selectionHydrated` flips once the saved selection is applied (or known
  // absent) — published on the gate so the mount's step-resume doesn't read
  // "targeting incomplete" off a selection that simply hasn't loaded yet.
  const hydrated = useRef(false);
  const [selectionHydrated, setSelectionHydrated] = useState(false);
  useEffect(() => {
    if (hydrated.current || citiesLoading || !savedFetched) return;
    const saved = (savedData as { selectedCities?: any[] } | undefined)?.selectedCities?.[0];
    const city = saved ? cities.find((c) => c.id === Number(saved.id)) : undefined;
    hydrated.current = true;
    if (!saved || !city) {
      // Nothing saved (fresh campaign) or the saved city is no longer offered —
      // the empty selection IS the settled state.
      setSelectionHydrated(true);
      return;
    }
    setSelectedCity(city);
    setSelectedAreas(
      (saved.addresses ?? []).map((a: any) => ({
        id: String(a.id),
        value: a.value,
        // Pins persist a `position`; polygons persist a `center` + `paths`.
        lat: a.position?.lat ?? a.center?.lat,
        lng: a.position?.lng ?? a.center?.lng,
        type: a.type,
        paths: a.paths,
        custom: !!a.isCustomLocationName,
        units: typeof a.missionCount === "number" ? a.missionCount : undefined,
        // H2H scheduling regroups the parallel `#`-strings back into the nested
        // date→times model; empty/absent (D2D) yields no schedule.
        schedule: unpackSchedule(a.startDate, a.time),
      }))
    );
    setSelectionHydrated(true);
    loadRecommendedZones(city.id);
  }, [savedData, savedFetched, cities, citiesLoading, loadRecommendedZones]);

  // Latest-write-wins persistence queue for the selection. Saves are full
  // snapshots, so only the newest payload matters: at most one request is in
  // flight, and a burst of add/remove clicks collapses into the next send.
  // This keeps the pathfinder write order equal to the click order — concurrent
  // full-replace saves could otherwise finish out of order and persist a stale
  // selection — and defers the server-cost refresh to a single refetch once the
  // queue drains. That refetch uses cancelRefetch: an invalidation landing while
  // a cost fetch is already in flight is otherwise deduped into it, and the
  // stale result would stick as the Summary price until the next edit.
  // Each payload carries its campaignId, so a drain loop started under one
  // closure generation can never send a later payload under a stale id.
  const pendingSelection = useRef<{
    campaignId: string;
    selectedCities: ReturnType<typeof toSelectedCity>[];
  } | null>(null);
  const persistDraining = useRef(false);
  // The drain loop always finishes its sends (persisting the final selection on
  // teardown is desirable); only the trailing refetch is skipped after unmount.
  const providerUnmounted = useRef(false);
  useEffect(
    () => () => {
      providerUnmounted.current = true;
    },
    []
  );
  const enqueueSelectionPersist = useCallback(
    (selectedCities: ReturnType<typeof toSelectedCity>[]) => {
      if (!campaignId) return;
      pendingSelection.current = { campaignId, selectedCities };
      if (persistDraining.current) return;
      persistDraining.current = true;
      void (async () => {
        let lastCampaignId = campaignId;
        try {
          while (pendingSelection.current) {
            const payload = pendingSelection.current;
            pendingSelection.current = null;
            lastCampaignId = payload.campaignId;
            // A failed send is surfaced via the save-status pill (trackSave);
            // keep draining so a newer selection still gets persisted after it.
            await trackSave(save, () =>
              updateSelfServeCampaignData({ campaignId: payload.campaignId, selectedCities: payload.selectedCities })
            ).catch(() => undefined);
          }
        } finally {
          persistDraining.current = false;
          // Refetch off the final server state (AC1/AC33) — also after a failed
          // send, so the Summary shows what the server actually holds.
          if (!providerUnmounted.current) {
            void queryClient.refetchQueries(["channelCampaignCost", lastCampaignId], undefined, {
              cancelRefetch: true,
            });
          }
        }
      })();
    },
    [campaignId, save, queryClient]
  );

  // client-copilot-v2 refetch-sync: re-apply the saved selection after the
  // chat agent wrote targeting through the Oppizi MCP. The mount rehydrate is
  // one-shot (hydrated ref), so a plain invalidation would refresh the cache
  // without touching the map — resetting the flag lets the existing hydration
  // effect re-apply the fresh server state silently (selectionHydrated stays
  // true; no step-gate flash). Skipped while the user's own latest-write-wins
  // save queue holds a pending/in-flight snapshot: their full-snapshot save is
  // about to overwrite the agent's write anyway (server is last-write-wins),
  // and reloading now would clobber in-flight UI state.
  const reloadSavedSelection = useCallback(() => {
    if (pendingSelection.current || persistDraining.current) return;
    hydrated.current = false;
    void queryClient.refetchQueries(savedQueryKey, undefined, { cancelRefetch: true });
    // savedQueryKey is derived from campaignId, which queryClient covers via
    // the register effect below re-running per campaign.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, campaignId]);
  useEffect(() => {
    if (!campaignId) return undefined;
    return registerTargetingReload(campaignId, reloadSavedSelection);
  }, [campaignId, reloadSavedSelection]);

  const selectCity = useCallback(
    (city: TargetingCity) => {
      if (selectedCity?.id === city.id) return;
      // Switching city clears the previous selection — areas belong to a city.
      setSelectedCity(city);
      setSelectedAreas([]);
      setRecommendedZones([]);
      // Drop any stale max-limit error from the previous city's selection.
      setBlockedLimit(null);

      if (campaignId) {
        const selectedCities = [toSelectedCity(city, [])];
        // Seed the saved-data cache so a remount before the refetch lands still
        // rehydrates the new city (switching city resets areas, hence empty).
        queryClient.setQueryData(savedQueryKey, { selectedCities });
        enqueueSelectionPersist(selectedCities);
      }

      loadRecommendedZones(city.id);
    },
    // savedQueryKey is derived from campaignId, so campaignId covers it.
    [selectedCity, campaignId, queryClient, enqueueSelectionPersist, loadRecommendedZones]
  );

  // Persists the selected areas for a city to pathfinder, seeding the saved-data
  // cache first so a remount before the refetch lands still rehydrates them.
  const persistAreas = useCallback(
    (city: TargetingCity, areas: TargetingArea[]) => {
      if (!campaignId) return;
      const selectedCities = [toSelectedCity(city, areas)];
      queryClient.setQueryData(savedQueryKey, { selectedCities });
      enqueueSelectionPersist(selectedCities);
    },
    [campaignId, queryClient, enqueueSelectionPersist]
  );

  const backfillAreaUnits = useCallback(
    (unitsById: Map<string, number>) => {
      if (!selectedCity) return;
      let changed = false;
      const next = selectedAreas.map((area) => {
        // A positive count is final (US carrier routes, healed drafts); 0 is the non-US
        // pathfinder placeholder and may be replaced by findNumberHouseholds (PAD-33564).
        if (typeof area.units === "number" && area.units > 0) return area;
        const units = unitsById.get(area.id);
        if (typeof units !== "number" || units === area.units) return area;
        changed = true;
        return { ...area, units };
      });
      if (!changed) return;
      setSelectedAreas(next);
      // Persist so the draft's missionCount is healed for the next rehydrate.
      persistAreas(selectedCity, next);
    },
    [selectedCity, selectedAreas, persistAreas]
  );

  const addArea = useCallback(
    (area: TargetingArea) => {
      if (!selectedCity || selectedAreas.some((a) => a.id === area.id)) return;
      // D2D caps the number of areas (AC32): block the add and surface the
      // inline error rather than letting the selection exceed the limit. H2H's
      // cap is on weekly distributors, enforced where dates/times are picked.
      if (channel === "D2D" && selectedAreas.length >= maximum) {
        setBlockedLimit(getMaxLimitMessage("D2D", maximum));
        return;
      }
      const next = [...selectedAreas, area];
      setSelectedAreas(next);
      persistAreas(selectedCity, next);
      setBlockedLimit(null);
      // User-initiated add → fire the shared "area added" toast. The nonce makes
      // re-adds re-trigger it; hydrate/removal never touch this.
      setJustAddedArea((prev) => ({ id: area.id, nonce: (prev?.nonce ?? 0) + 1 }));
    },
    [selectedCity, selectedAreas, persistAreas, channel, maximum]
  );

  const clearJustAdded = useCallback(() => setJustAddedArea(null), []);

  const removeArea = useCallback(
    (id: string) => {
      if (!selectedCity) return;
      const next = selectedAreas.filter((a) => a.id !== id);
      setSelectedAreas(next);
      persistAreas(selectedCity, next);
      setBlockedLimit(null);
    },
    [selectedCity, selectedAreas, persistAreas]
  );

  const setAreaSchedule = useCallback(
    (id: string, schedule: AreaScheduleEntry[]) => {
      if (!selectedCity) return;
      const distributors = countPairs(schedule) * baPerLocation;
      const next = selectedAreas.map((a) => (a.id === id ? { ...a, schedule, units: distributors } : a));
      // H2H weekly distributor cap (AC32): reject a change that pushes any week
      // past the limit, but always allow edits that reduce it (so an over-limit
      // draft can be brought back down). Distributors per week = pairs × BAs.
      const prospectiveWeeklyMax = maxWeeklyDistributors(next, baPerLocation);
      if (
        prospectiveWeeklyMax > maximum &&
        prospectiveWeeklyMax > maxWeeklyDistributors(selectedAreas, baPerLocation)
      ) {
        setBlockedLimit(getMaxLimitMessage("H2H", maximum));
        return;
      }
      setSelectedAreas(next);
      persistAreas(selectedCity, next);
      setBlockedLimit(null);
    },
    [selectedCity, selectedAreas, persistAreas, baPerLocation, maximum]
  );

  const highlightArea = useCallback(
    (id: string) => setHighlightTarget((prev) => ({ id, nonce: (prev?.nonce ?? 0) + 1 })),
    []
  );

  const focusOnMap = useCallback((id: string) => setMapFocusAreaId(id), []);
  const clearMapFocus = useCallback(() => setMapFocusAreaId(null), []);

  const setHoveredArea = useCallback((id: string | null) => setHoveredAreaId(id), []);

  const searchMapPoint = useCallback(
    (point: { lat: number; lng: number }) =>
      setMapSearchPoint((prev) => ({ lat: point.lat, lng: point.lng, nonce: (prev?.nonce ?? 0) + 1 })),
    []
  );
  const clearMapSearchPoint = useCallback(() => setMapSearchPoint(null), []);

  // Inline maximum error (AC32): a just-blocked attempt, or a rehydrated draft
  // already past the cap.
  const limitError = blockedLimit ?? (overMaximum ? getMaxLimitMessage(channel, maximum) : null);

  // Publish the hard-gate state up to the builder mount, which owns the Summary
  // CTA (disable + tooltip, AC31) and the min-budget modal (AC33) — both render
  // above this provider, so they can't read its state directly.
  const report = useTargetingGateReporter();
  const countryName = country?.name;
  const cityName = selectedCity?.name;
  const minMet = !!selectedCity && gateCount >= limits.minimum;
  // Server-authoritative pre-tax cost for the current selection (F15) — drives the
  // Summary figure and the below-market-minimum check (AC33), which stayed dormant
  // until a real subtotal existed.
  const subtotal = detailedCost?.subtotal ?? null;
  const belowMinBudget = isBelowMinimumCampaignValue(countryName, cityName, subtotal ?? undefined);
  const minBudgetFormatted = getMinimumCampaignValue(countryName, cityName)?.formatted ?? null;

  // Summary units: H2H total flyers (distributors × flyer-pack), D2D total
  // letterboxes (Σ per-area delivery counts). Derived locally so the Summary
  // updates the moment the selection changes (AC1), ahead of the cost refetch.
  const summaryUnits =
    channel === "H2H" ? countDistributors(selectedAreas) * flyerPack : countDistributors(selectedAreas);

  // AC35: a re-entered H2H draft whose per-area distribution dates now fall in
  // the past / within the configured lead time. Detected read-side only — the
  // calendar's minDate prevents picking invalid dates fresh. (D2D's launch-date
  // case is detected at the mount, which holds startDate.)
  const minLeadBusinessDays = useMinLeadTimeBusinessDays();
  // Memoized so the per-area scan (and the `new Date()` inside getEarliestLaunchDate)
  // only runs when the lead time or selection changes — not on every provider render.
  const outdatedAreaId = useMemo(() => {
    if (channel !== "H2H") return null;
    const earliest = getEarliestLaunchDate(minLeadBusinessDays);
    return selectedAreas.find((area) => scheduleHasOutdatedDate(area.schedule ?? [], earliest))?.id ?? null;
  }, [channel, minLeadBusinessDays, selectedAreas]);
  const datesOutdated = outdatedAreaId !== null;

  useEffect(() => {
    const gate: TargetingGate = {
      citySelected: !!selectedCity,
      minMet,
      maxExceeded: overMaximum,
      minTooltip: minMet ? null : getMinLimitMessage(channel, limits.minimum),
      belowMinBudget,
      minBudgetFormatted,
      datesOutdated,
      outdatedAreaId,
      summaryUnits,
      subtotal,
      hydrated: selectionHydrated,
    };
    report(gate);
  }, [
    report,
    channel,
    selectedCity,
    minMet,
    overMaximum,
    limits.minimum,
    belowMinBudget,
    minBudgetFormatted,
    datesOutdated,
    outdatedAreaId,
    summaryUnits,
    subtotal,
    selectionHydrated,
  ]);

  // F9: the warning's Edit link (in the mount, above this provider) asks us to
  // reveal the affected control. The H2H area card flashes here; the D2D
  // launch-date picker is opened by CampaignDetailsCard, which owns it. This
  // provider is stable now (above the shell, PAD-31560), so the nonce effect only
  // fires on a real change — no replay on step return.
  const { request: focusRequest } = useTargetingFocus();
  useEffect(() => {
    // The nonce is the sole trigger; target/areaId are read from the current
    // render's request, which always carries a fresh nonce when either changes
    // (the mount only ever bumps the nonce together with a new target/areaId).
    if (focusRequest?.target === "distributionAreas" && focusRequest.areaId) {
      highlightArea(focusRequest.areaId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nonce is the trigger
  }, [focusRequest?.nonce]);

  const value = useMemo<TargetingContextValue>(
    () => ({
      channel,
      cities,
      citiesLoading,
      citiesError,
      selectedCity,
      selectCity,
      isAreasLoading,
      recommendedZones,
      selectedAreas,
      setSelectedAreas,
      addArea,
      removeArea,
      setAreaSchedule,
      backfillAreaUnits,
      baPerLocation,
      flyerPack,
      justAddedArea,
      clearJustAdded,
      highlightTarget,
      highlightArea,
      mapFocusAreaId,
      focusOnMap,
      clearMapFocus,
      hoveredAreaId,
      setHoveredArea,
      mapSearchPoint,
      searchMapPoint,
      clearMapSearchPoint,
      limitError,
    }),
    [
      channel,
      cities,
      citiesLoading,
      citiesError,
      selectedCity,
      selectCity,
      isAreasLoading,
      recommendedZones,
      selectedAreas,
      addArea,
      removeArea,
      setAreaSchedule,
      backfillAreaUnits,
      baPerLocation,
      flyerPack,
      justAddedArea,
      clearJustAdded,
      highlightTarget,
      highlightArea,
      mapFocusAreaId,
      focusOnMap,
      clearMapFocus,
      hoveredAreaId,
      setHoveredArea,
      mapSearchPoint,
      searchMapPoint,
      clearMapSearchPoint,
      limitError,
    ]
  );

  return <TargetingContext.Provider value={value}>{children}</TargetingContext.Provider>;
};

export const useTargeting = () => {
  const context = useContext(TargetingContext);
  if (context === undefined) {
    throw new Error("useTargeting must be used within a TargetingProvider");
  }
  return context;
};
